const AUTH_DIALOG_SESSION_KEY = 'auth_dialog_context'
const PHONE_LOGIN_ENABLED = process.env.VUE_APP_ENABLE_PHONE_LOGIN === 'true'
const PASSWORD_LOGIN_ENABLED = process.env.VUE_APP_ENABLE_PASSWORD_LOGIN === 'true'
const DEFAULT_LOGIN_TAB = PHONE_LOGIN_ENABLED ? 'phone' : 'email'

const getSessionStorage = () => {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
}

const normalizeRedirect = (redirect) => {
  if (typeof redirect !== 'string') return ''
  const value = redirect.trim()
  return value.startsWith('/') && !value.startsWith('//') ? value : ''
}

const normalizeTab = (tab) => {
  if (tab === 'phone' && PHONE_LOGIN_ENABLED) return 'phone'
  if (tab === 'email') return 'email'
  if (tab === 'password' && PASSWORD_LOGIN_ENABLED) return 'password'
  return DEFAULT_LOGIN_TAB
}

const readPendingContext = () => {
  const storage = getSessionStorage()
  if (!storage) return {}

  try {
    return JSON.parse(storage.getItem(AUTH_DIALOG_SESSION_KEY) || '{}')
  } catch (error) {
    storage.removeItem(AUTH_DIALOG_SESSION_KEY)
    return {}
  }
}

const pendingContext = readPendingContext()

const state = () => ({
  visible: false,
  redirect: normalizeRedirect(pendingContext.redirect),
  reason: typeof pendingContext.reason === 'string' ? pendingContext.reason : '',
  inviteCode: typeof pendingContext.inviteCode === 'string' ? pendingContext.inviteCode : '',
  initialTab: normalizeTab(pendingContext.initialTab)
})

const getters = {
  visible: state => state.visible,
  redirect: state => state.redirect,
  reason: state => state.reason,
  inviteCode: state => state.inviteCode,
  initialTab: state => state.initialTab,
  pendingContext: state => ({
    redirect: state.redirect,
    reason: state.reason,
    inviteCode: state.inviteCode,
    initialTab: state.initialTab
  })
}

const mutations = {
  OPEN(state, payload = {}) {
    const wasVisible = state.visible
    state.visible = true
    if (Object.prototype.hasOwnProperty.call(payload, 'redirect')) {
      state.redirect = normalizeRedirect(payload.redirect)
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'reason')) {
      state.reason = typeof payload.reason === 'string' ? payload.reason : ''
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'inviteCode')) {
      state.inviteCode = typeof payload.inviteCode === 'string' ? payload.inviteCode.trim() : ''
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'initialTab')) {
      state.initialTab = normalizeTab(payload.initialTab)
    } else if (!wasVisible) {
      state.initialTab = DEFAULT_LOGIN_TAB
    }
  },
  CLOSE(state) {
    state.visible = false
  },
  SET_REDIRECT(state, redirect) {
    state.redirect = normalizeRedirect(redirect)
  },
  SET_REASON(state, reason) {
    state.reason = typeof reason === 'string' ? reason : ''
  },
  SET_INVITE_CODE(state, inviteCode) {
    state.inviteCode = typeof inviteCode === 'string' ? inviteCode.trim() : ''
  },
  SET_INITIAL_TAB(state, initialTab) {
    state.initialTab = normalizeTab(initialTab)
  },
  CLEAR_PENDING(state) {
    state.redirect = ''
    state.reason = ''
    state.inviteCode = ''
    state.initialTab = DEFAULT_LOGIN_TAB
  }
}

const persistPendingContext = (state) => {
  const storage = getSessionStorage()
  if (!storage) return

  storage.setItem(AUTH_DIALOG_SESSION_KEY, JSON.stringify({
    redirect: state.redirect,
    reason: state.reason,
    inviteCode: state.inviteCode,
    initialTab: state.initialTab
  }))
}

const clearPendingContext = () => {
  const storage = getSessionStorage()
  if (!storage) return
  storage.removeItem(AUTH_DIALOG_SESSION_KEY)
  storage.removeItem('invite_code')
}

const actions = {
  open({ commit, state }, payload = {}) {
    const options = typeof payload === 'string' ? { reason: payload } : payload
    commit('OPEN', options || {})
    persistPendingContext(state)
  },
  close({ commit, state }) {
    commit('CLOSE')
    persistPendingContext(state)
  },
  cancel({ commit }) {
    commit('CLOSE')
    commit('CLEAR_PENDING')
    clearPendingContext()
  },
  setRedirect({ commit, state }, redirect) {
    commit('SET_REDIRECT', redirect)
    persistPendingContext(state)
  },
  setReason({ commit, state }, reason) {
    commit('SET_REASON', reason)
    persistPendingContext(state)
  },
  setInviteCode({ commit, state }, inviteCode) {
    commit('SET_INVITE_CODE', inviteCode)
    persistPendingContext(state)
  },
  setInitialTab({ commit, state }, initialTab) {
    commit('SET_INITIAL_TAB', initialTab)
    persistPendingContext(state)
  },
  persist({ state }) {
    persistPendingContext(state)
    return {
      redirect: state.redirect,
      reason: state.reason,
      inviteCode: state.inviteCode,
      initialTab: state.initialTab
    }
  },
  loginSuccess({ commit, state }, payload = {}) {
    const context = {
      redirect: state.redirect,
      reason: state.reason,
      inviteCode: state.inviteCode,
      initialTab: state.initialTab,
      ...payload
    }
    commit('CLOSE')
    commit('CLEAR_PENDING')
    clearPendingContext()
    return context
  }
}

export { AUTH_DIALOG_SESSION_KEY }

export default {
  state,
  getters,
  mutations,
  actions
}
